// AdminPage.tsx
import React, { useEffect, useState } from 'react';
import { Box, Button, Spinner, Stack, useToast, Flex, Tooltip } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FormField, Notification, useSettings } from '../../stateManagement/SettingsContext';
import { ColorSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/ColorSettingsMolecule';
import { HeaderSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/HeaderSettingsMolecule';
import {
  MenuCard,
  MenuCardSettingsOrganism,
} from '../molecules/AdminPageSettingsMolecules/MenuCardSettingsMolecule';
import { FooterSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/FooterSettingsMolecule';
import { PopularArticlesSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/PopularArticlesSettingsMolecule';
import { LandingPagePreview } from './LandingPagePreview';
import { BannerSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/BannerSettingsMolecule';
import { SectionsSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/SectionsSettingsMolecule';
import { CustomButton } from '../atoms';
import { PlayerHubPagePreview } from './PlayerHubPagePreview';
import { TicketPagePreview } from './TicketPagePreview';
import { NewsArticle } from '../organisms/NewsSection';
import { FooterLink } from '../organisms/Footer';
import { TranslationData } from '../../interfaces';
import { TranslationsSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/TranslationsSettingsMolecule';
import { ContactUsPagePreview } from './ContactUsPagePreview';
import { ContactUsSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/ContactUsSettingsMolecule';
import { NotificationSettingsMolecule } from '../molecules/AdminPageSettingsMolecules/NotificationSettingsMolecule';
import { SectionPagePreview } from './SectionPagePreview';
import { FaqPagePreview } from './FaqPagePreview';
import { OneTrustGASettingsMolecule } from '../molecules/AdminPageSettingsMolecules/OneTrustGASettingsMolecule';
import { NewsDetailPreview } from './NewsDetailPreview';
import { SectionIconSettingsmolecule } from '../molecules/AdminPageSettingsMolecules/SectionIconSettingsmolecule';
import { useAuth } from '../../stateManagement/auth/useAuth';
import { GlobalUserData, initializeHelpshiftChat } from '../../utils';
import { appInsights } from '../../appInsights';
import { NewsAdminWorkspace } from '../molecules/AdminPageSettingsMolecules/NewsAdminWorkspace';

const PLATFORM_ID = process.env.REACT_APP_WEB_PLATFORM_ID || '';
const DOMAIN = process.env.REACT_APP_DOMAIN || '';
const LANGUAGE = process.env.REACT_APP_LANGUAGE || '';
// const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || '';

export const AdminPage: React.FC = () => {
  const {
    settings,
    setSettings,
    saveSettings,
    saveNewsArticlesOnly,
    sectionsData,
    supportedLanguages,
    setLanguage,
    cifsData,
    contactUsFormData,
    setAdminApiKey,
    adminPanelStructData,
    newsArticles,
  } = useSettings();

  const toast = useToast();
  const { t } = useTranslation();
  const [localSettings, setLocalSettings] = useState(settings);
  const [localNewsArticles, setLocalNewsArticles] = useState<NewsArticle[]>(newsArticles || []);
  const [contactUsData, setContactUsData] = useState<FormField[]>(contactUsFormData ?? []);
  const [cifData] = useState(cifsData);
  const [disableSaveSettings, setDisableSaveSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isNewsArticleSaving, setIsNewsArticleSaving] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [titleForSection, setTitleForSection] = useState('');
  const [titleForFaq, setTitleForFaq] = useState<string>('');
  const [newsId, setNewsId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<
    'branding' | 'data' | 'translations' | 'contactUs' | 'oneTrust & GA'
  >('branding');
  const [activeBrandingTab, setActiveBrandingTab] = useState<
    'color' | 'header' | 'banner' | 'footer' | 'sections'
  >('color');
  const [activeDataTab, setActiveDataTab] = useState<
    'menu' | 'news' | 'popular' | 'notification' | 'icons'
  >('menu');
  const [currentPage, setCurrentPage] = useState<
    'playerHub' | 'landing' | 'ticket' | 'contactUs' | 'sectionPage' | 'faqPage' | 'newsPage'
  >('landing');
  const { i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [showLoader, setShowLoader] = useState(true);
  // const [showOverlay, setShowOverlay] = useState(false);
  const [adminName, setAdminName] = useState<string>('');
  const [adminEmail, setAdminEmail] = useState<string>('');
  // const adminName = 'Test';
  // const adminEmail = 'test@gmail.com';

  const { user, accessToken } = useAuth();

  const userId: string = user?.idTokenClaims?.sub as string;
  const hsUserEmail = user?.idTokenClaims?.emails?.[0] || '';
  const hsUserName = user?.idTokenClaims?.name || '';
  // const steamId = user?.idTokenClaims?.steam_id as string;
  const globalUserData: GlobalUserData = {
    first_name: String(user?.idTokenClaims?.given_name || ''),
    last_name: String(user?.idTokenClaims?.family_name || ''),
    full_name: String(user?.idTokenClaims?.name || ''),
    display_name: String(user?.idTokenClaims?.name || ''),
    last_country: String(user?.idTokenClaims?.country || ''),
    last_city: String(user?.idTokenClaims?.city || ''),
    age: String(user?.idTokenClaims?.age || ''),
    lifetime_value: String(user?.idTokenClaims?.lifetimeValue || ''),
    user_persona: String(user?.idTokenClaims?.userPersona || ''),
    user_vip_segment: String(user?.idTokenClaims?.userVipSegment || ''),
    user_support_status: String(user?.idTokenClaims?.userSupportStatus || ''),
    last_active_date: String(user?.idTokenClaims?.lastActiveDate || ''),
    accepted_t_and_c: String(user?.idTokenClaims?.acceptedTAndC || false),
    preferred_language: String(user?.idTokenClaims?.preferredLanguage || ''),
  };
  useEffect(() => {
    if (user && userId && accessToken) {
      initializeHelpshiftChat({
        platformId: PLATFORM_ID,
        domain: DOMAIN,
        language: LANGUAGE,
        userName: hsUserName,
        userEmail: hsUserEmail,
        userId,
        // steam_id: steamId,
        globalUserData,
        accessToken,
        property: 'hideWidget',
        // userAuthToken: hmac(userId, hsUserEmail, SECRET_KEY),
      });
    }
  }, [accessToken]);

  const onClickMore = (title: string) => {
    setTitleForSection(title);
    setCurrentPage('sectionPage');
  };
  const onClickFaq = (publish_id: string) => {
    setTitleForFaq(publish_id);
    setCurrentPage('faqPage');
  };
  const onClickPopularArticlePreview = (publish_id: string | number) => {
    setTitleForFaq(String(publish_id));
    setCurrentPage('faqPage');
  };
  const onClickNewsArticlePreview = (id: string | number) => {
    setNewsId(String(id));
    setCurrentPage('newsPage');
  };

  useEffect(() => {
    if (activeTab === 'translations') {
      setDisableSaveSettings(true);
    } else {
      setDisableSaveSettings(false);
    }
  }, [activeTab]);

  const allowedOrigins = [
    'https://ravi-demo.helpshift.com',
    'https://suraj-demo.helpshift.com',
    'https://catalyst.helpshift.com',
  ];
  const [checkedIframe, setCheckedIframe] = useState(false);

  // Iframe: Ask parent for data
  useEffect(() => {
    window.parent.postMessage({ type: 'request-hs-data' }, '*'); // Or use specific origin
    setShowLoader(false);
  }, []);

  useEffect(() => {
    const isInIframe = window.top !== window.self;

    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'object' || event.data === null) return;
      const { type, payload } = event.data;

      if (type === 'hs-data' && payload) {
        const { origin, apiKey, userEmail, userName } = payload;
        // eslint-disable-next-line no-console
        setAdminEmail(userEmail);
        setAdminName(userName);

        // Validate parent origin
        if (!allowedOrigins.includes(origin)) {
          window.location.replace(`${window.origin}/notfound`);
          return;
        }

        setAdminApiKey(btoa(apiKey));
      }
    };

    window.addEventListener('message', handleMessage);

    if (!isInIframe) {
      // console.error('Blocked: not in iframe');
      window.location.replace(`${window.origin}/notfound`);
    } else {
      setCheckedIframe(true);
    }

    // eslint-disable-next-line
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const initialTranslationData: TranslationData = {
    configs: {
      needHelpText: t('configs.needHelpText'),
    },
    data: {
      gameTitle: t('data.gameTitle'),
      platforms: t('data.platforms', { returnObjects: true }) as string[],
      // newsArticles intentionally omitted — managed independently via newsArticles.json
      newsArticles: [],
      popularArticles: t('data.popularArticles', { returnObjects: true }) as {
        id: string;
        title: string;
        category: string;
      }[],
      menuCards: t('data.menuCards', { returnObjects: true }) as {
        id: string;
        title: string;
      }[],
      footerLinks: t('data.footerLinks', { returnObjects: true }) as {
        id: number;
        text: string;
      }[],
      sectionIcons: t('data.sectionIcons', { returnObjects: true }) as {
        id: string;
        name: string;
      }[],
      categoryHeadings: t('data.categoryHeadings', { returnObjects: true }) as {
        [key: string]: string;
      }[],
      helperTexts: t('data.helperTexts', { returnObjects: true }) as {
        [key: string]: string;
      }[],
      contactUsFormData: t('data.contactUsFormData', { returnObjects: true }) as {
        id: string;
        question: string;
        description?: string;
        placeholder?: string;
        options?: string;
      }[],
      footerNote: t('data.footerNote'),
      notifications: t('data.notifications', { returnObjects: true }) as {
        id: string;
        message: string;
      }[],
      navLinks: t('data.navLinks', { returnObjects: true }) as {
        [key: string]: string;
      }[],
    },
  };

  const [translationData, setTranslationData] = useState<TranslationData>(initialTranslationData);

  useEffect(() => {
    if (selectedLanguage !== 'en') {
      i18n
        .changeLanguage('en')
        .then(() => {
          setSelectedLanguage('en');
          setLanguage('en');
          setTranslationData({
            configs: { needHelpText: t('configs.needHelpText') },
            data: {
              gameTitle: t('data.gameTitle'),
              platforms: t('data.platforms', { returnObjects: true }) as string[],
              newsArticles: [],
              popularArticles: t('data.popularArticles', { returnObjects: true }) as {
                id: string;
                title: string;
                category: string;
              }[],
              menuCards: t('data.menuCards', { returnObjects: true }) as {
                id: string;
                title: string;
              }[],
              footerLinks: t('data.footerLinks', { returnObjects: true }) as {
                id: number;
                text: string;
              }[],
              sectionIcons: t('data.sectionIcons', { returnObjects: true }) as {
                id: string;
                name: string;
              }[],
              categoryHeadings: t('data.categoryHeadings', { returnObjects: true }) as {
                [key: string]: string;
              }[],
              helperTexts: t('data.helperTexts', { returnObjects: true }) as {
                [key: string]: string;
              }[],
              contactUsFormData: t('data.contactUsFormData', { returnObjects: true }) as {
                id: string;
                question: string;
                description?: string;
                placeholder?: string;
                options?: string;
              }[],
              footerNote: t('data.footerNote'),
              notifications: t('data.notifications', { returnObjects: true }) as {
                id: string;
                message: string;
              }[],
              navLinks: t('data.navLinks', { returnObjects: true }) as {
                [key: string]: string;
              }[],
            },
          });
        })
        .catch(err => {
          // eslint-disable-next-line
          console.log('Error changing language:', err);
        });
    }
  }, []);

  const handleTranslationDataChange: (
    section: keyof TranslationData,
    key: string,
    value: any,
  ) => void = (section, key, value) => {
    setTranslationData(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  // ── newsCards is now just localNewsArticles directly.
  // No translation merge needed — newsArticles.json is the single source of truth.
  const newsCards: NewsArticle[] = localNewsArticles;

  const notifications: Notification[] = translationData.data.notifications.map(
    translatedNotification => {
      const matchingLocalNotif = localSettings?.data.notifications.find(
        localNotif => localNotif.id === translatedNotification.id,
      );
      return {
        ...(matchingLocalNotif ?? ''),
        ...(translatedNotification ?? ''),
        startTime: matchingLocalNotif?.startTime ?? '',
        endTime: matchingLocalNotif?.endTime ?? '',
      };
    },
  );

  const translatedFooterLinks: FooterLink[] =
    settings?.data?.footerLinks.map(link => {
      const translation = translationData.data.footerLinks.find(i => i.id === link.id);
      return { ...link, text: translation ? translation.text : link.text };
    }) || [];

  const menuCards: MenuCard[] = translationData.data.menuCards.map(translatedCard => {
    const matchingLocalCard = localSettings?.data.menuCards.find(
      localCard => localCard.id === translatedCard.id,
    );
    return {
      ...matchingLocalCard,
      ...translatedCard,
      menuCardLink: matchingLocalCard?.menuCardLink ?? '',
      icon: matchingLocalCard?.icon ?? '',
    };
  });

  // ── News article change: only updates localNewsArticles (no translation sync).
  // Called by workspace AFTER a successful API save.
  const handleNewsArticleChange = (updatedNewsArticles: NewsArticle[]) => {
    setLocalNewsArticles(updatedNewsArticles);
  };

  // ── Dedicated handler for per-article save from the News workspace.
  // The workspace awaits this — it only updates local state on success.
  const handleSaveNewsArticle = async (updatedList: NewsArticle[]) => {
    setIsNewsArticleSaving(true);
    try {
      await saveNewsArticlesOnly(updatedList);
    } finally {
      setIsNewsArticleSaving(false);
    }
  };

  const handleNotificationsChange = (updatedNotifs: Notification[]) => {
    setLocalSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          notifications: updatedNotifs.map(notifs => {
            // Update title separately in translations
            handleTranslationDataChange('data', 'notifications', updatedNotifs);
            return notifs; // Update everything else in localSettings
          }),
        },
      };
    });
  };

  const handleFooterLinksSettingsChange = (updatedFooterLinks: FooterLink[]) => {
    setLocalSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          footerLinks: updatedFooterLinks.map(footerLinks => {
            // Update title separately in translations
            handleTranslationDataChange('data', 'footerLinks', updatedFooterLinks);
            return footerLinks; // Update everything else in localSettings
          }),
        },
      };
    });
  };

  const handleFooterTextSettingsChange = (
    category: keyof TranslationData,
    updatedTextKey: string,
    updatedTextValue: string,
  ) => {
    setLocalSettings(prev => {
      if (!prev) return prev;
      const updatedSettings = {
        ...prev,
        [category]: { ...prev.data, [updatedTextKey]: updatedTextValue },
      };
      handleTranslationDataChange(category, updatedTextKey, updatedTextValue);
      return updatedSettings;
    });
  };

  type Diff =
    | { path: string; type: 'changed'; before: any; after: any }
    | { path: string; type: 'added'; value: any }
    | { path: string; type: 'removed'; value: any };

  function getDiff(obj1: any, obj2: any, path = ''): Diff[] {
    const diffs: Diff[] = [];
    const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
    Array.from(allKeys).forEach(key => {
      const fullPath = path ? `${path}.${key}` : key;
      const val1 = obj1?.[key];
      const val2 = obj2?.[key];
      if (!(key in obj2)) {
        diffs.push({ path: fullPath, type: 'removed', value: val1 });
      } else if (!(key in obj1)) {
        diffs.push({ path: fullPath, type: 'added', value: val2 });
      } else if (Array.isArray(val1) && Array.isArray(val2)) {
        const map1 = new Map(val1.map((v: any) => [v.id ?? JSON.stringify(v), v]));
        const map2 = new Map(val2.map((v: any) => [v.id ?? JSON.stringify(v), v]));
        Array.from(map1.entries()).forEach(([k, v]) => {
          if (!map2.has(k)) {
            diffs.push({ path: fullPath, type: 'removed', value: v });
          } else {
            const v2 = map2.get(k)!;
            if (typeof v === 'object' && v !== null && typeof v2 === 'object' && v2 !== null) {
              diffs.push(...getDiff(v, v2, `${fullPath}[id=${k}]`));
            } else if (v !== v2) {
              diffs.push({ path: `${fullPath}[id=${k}]`, type: 'changed', before: v, after: v2 });
            }
          }
        });
        Array.from(map2.entries()).forEach(([k, v]) => {
          if (!map1.has(k)) {
            diffs.push({ path: fullPath, type: 'added', value: v });
          }
        });
      } else if (
        typeof val1 === 'object' &&
        val1 !== null &&
        typeof val2 === 'object' &&
        val2 !== null
      ) {
        diffs.push(...getDiff(val1, val2, fullPath));
      } else if (JSON.stringify(val1) !== JSON.stringify(val2)) {
        diffs.push({ path: fullPath, type: 'changed', before: val1, after: val2 });
      }
    });
    return diffs;
  }

  function findPathDescription(
    obj: any,
    target: string,
    currentPath: string[] = [],
  ): string | null {
    let found: string | null = null;
    Object.entries(obj).forEach(([key, value]) => {
      if (found) return;
      if (typeof value === 'object') {
        const result = findPathDescription(value, target, [...currentPath, key]);
        if (result) found = result;
      } else if (value === target) {
        found = [...currentPath, key].join(' inside ');
      }
    });
    return found;
  }

  // ── Full settings save (branding, contact-us, etc.) — does NOT save newsArticles
  const handleSave = async (userEmail?: string, userName?: string) => {
    if (!localSettings) return;
    setIsSaving(true);
    try {
      setSettings(localSettings);
      setTranslationData(translationData);

      const settingsDiffs = getDiff(settings, localSettings);
      const contactUsDiffs = getDiff(
        { contactUsList: contactUsFormData || [] },
        { contactUsList: contactUsData },
      );
      const allDiffs = [...settingsDiffs, ...contactUsDiffs];

      if (allDiffs.length > 0) {
        const lines: string[] = [];
        if (userEmail && userName) {
          lines.push(`Changes made by: ${userName} (${userEmail})\n`);
        }
        allDiffs.forEach(diff => {
          let pathText = '';
          if (diff.path.startsWith('contactUsList[')) {
            const cleanPath = diff.path.replace(/^contactUsList\[id=(.+?)\]/, '$1');
            pathText = `Contact Us Form: ${cleanPath}`;
          } else {
            const key = diff.path.split('.').pop();
            const labelPath = findPathDescription(adminPanelStructData, key ?? '');
            const readable = labelPath || diff.path;
            const segments = readable.split(' inside ');
            const first = segments[0];
            const middle = segments.slice(1, -1).join('" inside "');
            const last = segments[segments.length - 1];
            pathText = middle
              ? `Under "${first}", inside "${middle}", "${last}"`
              : `Under "${first}", "${last}"`;
          }
          const formatValue = (val: any) =>
            typeof val === 'object' && val !== null ? JSON.stringify(val, null, 2) : String(val);
          if (diff.type === 'changed') {
            lines.push(
              `• ${pathText} was changed from "${formatValue(diff.before)}" to "${formatValue(diff.after)}"`,
            );
          } else if (diff.type === 'added') {
            lines.push(`• ${pathText} was added with value "${formatValue(diff.value)}"`);
          } else if (diff.type === 'removed') {
            lines.push(`• ${pathText} was removed (previous value: "${formatValue(diff.value)}")`);
          }
        });

        // Save first
        await saveSettings(localSettings, translationData, contactUsData, localNewsArticles);

        appInsights.trackEvent({
          name: 'AdminPanel_SettingsChanged',
          properties: {
            userName: userName || 'Unknown User',
            userEmail: userEmail || 'Unknown Email',
            changes: lines.join('\n'),
            status: 'Success',
          },
        });

        toast({
          title: 'Settings saved successfully!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      appInsights.trackEvent({
        name: 'AdminPanel_SettingsChanged',
        properties: {
          userName: userName || 'Unknown User',
          userEmail: userEmail || 'Unknown Email',
          changes: 'Save failed',
          status: 'Error',
        },
      });
      toast({
        title: 'Error saving settings',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfigChange = (key: string, value: any) => {
    setLocalSettings(prev => ({
      ...prev!,
      configs: { ...prev!.configs, [key]: value },
    }));
  };

  const onClickMyTickets = (): void => {
    setCurrentPage('playerHub');
  };
  const onClickLandingPageButton = (): void => {
    setCurrentPage('landing');
  };
  const onTicketClick = (): void => {
    setCurrentPage('ticket');
  };
  const onContactUsClick = (): void => {
    setCurrentPage('contactUs');
  };

  const handleMenuCardChange = (updatedMenuCards: any) => {
    setLocalSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          menuCards: updatedMenuCards.map((menuCard: any) => {
            handleTranslationDataChange('data', 'menuCards', updatedMenuCards);
            return menuCard;
          }),
        },
      };
    });
  };

  const handleContactUsFormDataChange = (updatedContactUsFormData: FormField[]) => {
    setContactUsData(updatedContactUsFormData);
    handleTranslationDataChange('data', 'contactUsFormData', updatedContactUsFormData);
  };

  const handlePopularFaqArticleChange = (updatedPopularFaqArticles: any[]) => {
    setLocalSettings(prev => {
      if (!prev) return prev;
      handleTranslationDataChange('data', 'popularArticles', updatedPopularFaqArticles);
      return { ...prev, data: { ...prev.data, popularArticles: updatedPopularFaqArticles } };
    });
  };

  const convertImageToBase64 = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const mainTabStyle = (tab: typeof activeTab) => ({
    bg: 'transparent',
    color: activeTab === tab ? 'blue.500' : 'gray.500',
    _hover: { bg: 'transparent', color: 'blue.600' },
    fontWeight: 'bold',
    minW: 'unset',
    flexShrink: 1,
    px: { base: 2, md: 4 },
    fontSize: { base: 'xs', md: 'sm' },
  });

  if (!localSettings || showLoader) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  const isNewsWorkspace = activeTab === 'data' && activeDataTab === 'news';

  return (
    <Box p={4}>
      {checkedIframe && (
        <Box id="outer" display="flex" flexDirection="column" height="100vh" overflow="hidden">
          {/* ── MAIN NAV BAR ──────────────────────────────────────────────────── */}
          <Box bg="white" borderBottom="1px solid" borderColor="gray.200" px={6} py={0}>
            <Flex align="center" justify="space-between" height="52px">
              <Flex align="center" height="100%">
                <Box
                  fontWeight="extrabold"
                  fontSize="xl"
                  letterSpacing="tight"
                  color="#2663eb"
                  flexShrink={0}
                  mr={4}
                >
                  ADMIN PANEL
                </Box>

                {(
                  [
                    { key: 'branding', label: 'Branding' },
                    { key: 'data', label: 'Content' },
                    { key: 'translations', label: 'Translations' },
                    { key: 'contactUs', label: 'Contact Us' },
                    { key: 'oneTrust & GA', label: 'OneTrust & GA' },
                  ] as { key: typeof activeTab; label: string }[]
                ).map(({ key, label }) => (
                  <Box
                    key={key}
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <CustomButton
                      onClick={() => {
                        setActiveTab(key);
                        if (key === 'contactUs') setCurrentPage('contactUs');
                        if (key === 'oneTrust & GA') setCurrentPage('landing');
                      }}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...mainTabStyle(key)}
                    >
                      {label}
                    </CustomButton>
                    <Box height="2px" bg={activeTab === key ? '#2663eb' : 'transparent'} />
                  </Box>
                ))}
              </Flex>

              <Flex align="center" gap={10}>
                <Flex bg="gray.100" borderRadius="lg" p={1} gap={1}>
                  <Box
                    px={3}
                    py={1}
                    borderRadius="md"
                    bg="white"
                    boxShadow="sm"
                    fontSize="xs"
                    fontWeight="bold"
                    color="#2663eb"
                    cursor="default"
                  >
                    ADMIN MODE
                  </Box>
                  <Tooltip label="Coming Soon" placement="bottom" fontSize="xs">
                    <Box
                      px={3}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="bold"
                      color="gray.400"
                      cursor="not-allowed"
                    >
                      WRITER MODE
                    </Box>
                  </Tooltip>
                </Flex>
                <Flex align="center" gap={2}>
                  <Box fontSize="sm" fontWeight="medium" color="gray.600">
                    {adminName || 'Admin User'}
                  </Box>
                  <Flex
                    w={8}
                    h={8}
                    borderRadius="full"
                    bg="blue.100"
                    align="center"
                    justify="center"
                    color="blue.600"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {(adminName || 'A').charAt(0).toUpperCase()}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Box>

          {/* ── SUB TABS for Content ─────────────────────────────────────────── */}
          {activeTab === 'data' && (
            <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={6}>
              <Flex gap={6} align="center">
                {(
                  [
                    { key: 'menu', label: 'Menu Cards' },
                    { key: 'news', label: 'News' },
                    { key: 'popular', label: 'Popular Articles' },
                    { key: 'notification', label: 'Notifications' },
                  ] as { key: typeof activeDataTab; label: string }[]
                ).map(({ key, label }) => (
                  <Box key={key}>
                    <CustomButton
                      onClick={() => setActiveDataTab(key)}
                      bg="transparent"
                      color={activeDataTab === key ? 'gray.900' : 'gray.500'}
                      fontWeight={activeDataTab === key ? 'bold' : 'medium'}
                      fontSize="sm"
                      _hover={{ bg: 'transparent', color: 'gray.900' }}
                      minW="unset"
                      px={0}
                    >
                      {label}
                    </CustomButton>
                    <Box
                      height="2px"
                      bg={activeDataTab === key ? 'gray.900' : 'transparent'}
                      mt={1}
                    />
                  </Box>
                ))}
              </Flex>
            </Box>
          )}

          {/* ── SUB TABS for Branding ────────────────────────────────────────── */}
          {activeTab === 'branding' && (
            <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={6} py={2}>
              <Flex gap={6}>
                {(
                  [
                    { key: 'color', label: 'Color' },
                    { key: 'header', label: 'Header' },
                    { key: 'banner', label: 'Banner' },
                    { key: 'footer', label: 'Footer' },
                    { key: 'sections', label: 'Section Banners' },
                  ] as { key: typeof activeBrandingTab; label: string }[]
                ).map(({ key, label }) => (
                  <Box key={key}>
                    <CustomButton
                      onClick={() => setActiveBrandingTab(key)}
                      bg="transparent"
                      color={activeBrandingTab === key ? 'gray.900' : 'gray.500'}
                      fontWeight={activeBrandingTab === key ? 'bold' : 'medium'}
                      fontSize="sm"
                      _hover={{ bg: 'transparent', color: 'gray.900' }}
                      minW="unset"
                      px={0}
                    >
                      {label}
                    </CustomButton>
                    <Box
                      height="2px"
                      bg={activeBrandingTab === key ? 'gray.900' : 'transparent'}
                      mt={1}
                    />
                  </Box>
                ))}
              </Flex>
            </Box>
          )}

          {/* ── CONTENT AREA ────────────────────────────────────────────────── */}
          <Box flex="1" overflow="hidden">
            {isNewsWorkspace ? (
              <NewsAdminWorkspace
                newsCards={newsCards}
                onNewsArticleChange={handleNewsArticleChange}
                onSaveArticle={handleSaveNewsArticle}
                settings={localSettings}
                isSaving={isNewsArticleSaving}
              />
            ) : (
              <Flex direction={['column', 'row']} gap={6} p={4} height="100%" overflow="hidden">
                {/* Left settings panel */}
                <Box
                  bg="gray.50"
                  p={4}
                  borderRadius="md"
                  boxShadow="sm"
                  width={['100%', '33%']}
                  overflowY="auto"
                >
                  <Stack spacing={6}>
                    {activeTab === 'branding' && (
                      <>
                        {activeBrandingTab === 'color' && (
                          <ColorSettingsMolecule
                            configs={localSettings.configs}
                            onConfigChange={handleConfigChange}
                          />
                        )}
                        {activeBrandingTab === 'header' && (
                          <HeaderSettingsMolecule
                            configs={localSettings.configs}
                            onConfigChange={handleConfigChange}
                            convertImageToBase64={convertImageToBase64}
                          />
                        )}
                        {activeBrandingTab === 'banner' && (
                          <BannerSettingsMolecule
                            configs={localSettings.configs}
                            onConfigChange={handleConfigChange}
                            convertImageToBase64={convertImageToBase64}
                          />
                        )}
                        {activeBrandingTab === 'footer' && (
                          <FooterSettingsMolecule
                            configs={localSettings.configs}
                            onConfigChange={handleConfigChange}
                            convertImageToBase64={convertImageToBase64}
                            translatedFooterLinks={translatedFooterLinks}
                            handleFooterLinksSettingsChange={handleFooterLinksSettingsChange}
                            handleFooterTextSettingsChange={handleFooterTextSettingsChange}
                            translation={translationData}
                          />
                        )}
                        {activeBrandingTab === 'sections' && (
                          <SectionsSettingsMolecule
                            configs={localSettings.configs}
                            onConfigChange={handleConfigChange}
                            configsForData={sectionsData}
                          />
                        )}
                      </>
                    )}

                    {activeTab === 'data' && (
                      <>
                        {activeDataTab === 'menu' && (
                          <MenuCardSettingsOrganism
                            configs={localSettings.configs}
                            onConfigChange={handleConfigChange}
                            menuCards={menuCards}
                            onMenuCardChange={handleMenuCardChange}
                          />
                        )}
                        {activeDataTab === 'popular' && (
                          <PopularArticlesSettingsMolecule
                            sectionsData={sectionsData}
                            data={localSettings?.data}
                            configs={localSettings?.configs}
                            onSelectionChange={handlePopularFaqArticleChange}
                            onConfigChange={handleConfigChange}
                          />
                        )}
                        {activeDataTab === 'icons' && (
                          <SectionIconSettingsmolecule
                            configs={localSettings.configs}
                            onConfigChange={handleConfigChange}
                          />
                        )}
                        {activeDataTab === 'notification' && (
                          <NotificationSettingsMolecule
                            configs={localSettings.configs}
                            onConfigChange={handleConfigChange}
                            onNotificationChange={handleNotificationsChange}
                            notifications={notifications}
                          />
                        )}
                      </>
                    )}

                    {activeTab === 'translations' && (
                      <TranslationsSettingsMolecule
                        supportedLanguages={supportedLanguages}
                        translationData={initialTranslationData}
                      />
                    )}

                    {activeTab === 'contactUs' && (
                      <ContactUsSettingsMolecule
                        cifs={cifData}
                        contactUsFormData={contactUsData}
                        onFormCardChange={handleContactUsFormDataChange}
                      />
                    )}

                    {activeTab === 'oneTrust & GA' && (
                      <OneTrustGASettingsMolecule
                        onConfigChange={handleConfigChange}
                        configs={localSettings.configs}
                        setDisableSaveButton={setDisableSaveButton}
                      />
                    )}
                  </Stack>
                </Box>

                {/* Right preview panel */}
                <Box
                  width={['100%', '65%']}
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                >
                  {!disableSaveSettings && (
                    <Box mb={2}>
                      <Button
                        onClick={() => handleSave(adminEmail, adminName)}
                        isLoading={isSaving}
                        colorScheme="blue"
                        size="sm"
                        isDisabled={disableSaveButton}
                      >
                        Save Settings
                      </Button>
                    </Box>
                  )}

                  <Box
                    bg="gray.100"
                    p={4}
                    borderRadius="md"
                    boxShadow="sm"
                    width="100%"
                    overflowY="scroll"
                    flex="1"
                  >
                    {currentPage === 'playerHub' && (
                      <PlayerHubPagePreview
                        settings={localSettings}
                        onTicketClick={onTicketClick}
                        onClickMyTickets={onClickMyTickets}
                        onClickLandingPageButton={onClickLandingPageButton}
                        onContactUsClick={onContactUsClick}
                      />
                    )}
                    {currentPage === 'landing' && (
                      <LandingPagePreview
                        settings={localSettings}
                        newsArticles={localNewsArticles}
                        translations={translationData}
                        sectionsData={sectionsData}
                        onClickMyTickets={onClickMyTickets}
                        onClickLandingPageButton={onClickLandingPageButton}
                        onContactUsClick={onContactUsClick}
                        onClickMore={onClickMore}
                        onClickFaq={onClickFaq}
                        onClickPopularArticlePreview={onClickPopularArticlePreview}
                        onClickNewsArticlePreview={onClickNewsArticlePreview}
                      />
                    )}
                    {currentPage === 'ticket' && (
                      <TicketPagePreview
                        settings={localSettings}
                        onClickMyTickets={onClickMyTickets}
                        onClickLandingPageButton={onClickLandingPageButton}
                        onContactUsClick={onContactUsClick}
                      />
                    )}
                    {currentPage === 'contactUs' && (
                      <ContactUsPagePreview
                        settings={localSettings}
                        contactUsFormData={contactUsData}
                        supportedLanguages={supportedLanguages}
                        onClickMyTickets={onClickMyTickets}
                        onClickLandingPageButton={onClickLandingPageButton}
                      />
                    )}
                    {currentPage === 'sectionPage' && (
                      <SectionPagePreview
                        settings={localSettings}
                        onContactUsClick={onContactUsClick}
                        onClickMyTickets={onClickMyTickets}
                        onClickLandingPageButton={onClickLandingPageButton}
                        titleForSection={titleForSection}
                        secondaryAccentColor={settings?.configs.secondaryAccentColor}
                      />
                    )}
                    {currentPage === 'faqPage' && (
                      <FaqPagePreview
                        settings={localSettings}
                        onContactUsClick={onContactUsClick}
                        onClickMyTickets={onClickMyTickets}
                        onClickLandingPageButton={onClickLandingPageButton}
                        onClickMore={onClickMore}
                        titleForFaq={titleForFaq}
                      />
                    )}
                    {currentPage === 'newsPage' && (
                      <NewsDetailPreview
                        newsId={newsId}
                        newsArticles={localNewsArticles}
                        settings={localSettings}
                        onContactUsClick={onContactUsClick}
                        onClickMyTickets={onClickMyTickets}
                        onClickLandingPageButton={onClickLandingPageButton}
                      />
                    )}
                  </Box>
                </Box>
              </Flex>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
